import { z } from 'zod';

/**
 * Distance display unit of a signed-in user (§15 #487, User.distanceUnit). Every distance is
 * stored and sent in metres; screens convert for display only. Signed-out viewers and caddies
 * (no account) see metres.
 */
export const DistanceUnitSchema = z.enum(['METERS', 'YARDS']);
export type DistanceUnit = z.infer<typeof DistanceUnitSchema>;

export const YARDS_PER_METER = 1 / 0.9144;

/** metres → the unit's number (not rounded) */
export function metersTo(meters: number, unit: DistanceUnit): number {
  return unit === 'YARDS' ? meters * YARDS_PER_METER : meters;
}

/** the unit's number → metres (a yards input stored as metres) */
export function toMeters(value: number, unit: DistanceUnit): number {
  return unit === 'YARDS' ? value / YARDS_PER_METER : value;
}

/** short unit label: "m" / "yd" */
export function distanceUnitLabel(unit: DistanceUnit): string {
  return unit === 'YARDS' ? 'yd' : 'm';
}

/**
 * "252.6 m" / "276.2 yd" — `digits` decimals (default 1; nearest-pin records often use 2),
 * formatted with the UI language's digits when `lng` is given.
 */
export function formatDistance(
  meters: number,
  unit: DistanceUnit = 'METERS',
  opts: { digits?: number; lng?: string } = {},
): string {
  const digits = opts.digits ?? 1;
  const v = metersTo(meters, unit);
  const n = opts.lng
    ? new Intl.NumberFormat(opts.lng, {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      }).format(v)
    : v.toFixed(digits);
  return `${n} ${distanceUnitLabel(unit)}`;
}
